import { readCache, writeCache, clearCache } from './cache'

// Calls the same-origin Pages Function /api/issues which fetches GitHub
// server-side with PAT (works for private repos) and strips emails from body.
const API_BASE = import.meta.env.VITE_API_BASE ?? ''
const ISSUES_ENDPOINT = `${API_BASE}/api/issues`
const CACHE_KEY = 'forms:approved-issues:v2'
// 10 min on the client; edge cache adds another 5 min layer. Worst-case
// staleness for a freshly approved item ≈ 10 min (or instant via refresh).
const TTL_MS = 10 * 60 * 1000
const FETCH_TIMEOUT_MS = 10_000

export interface Issue {
  number: number
  title: string
  html_url: string
  body: string
  created_at: string
  state: 'open' | 'closed'
  labels: { name: string }[]
  reactions: { total_count: number; '+1': number }
}

export async function fetchApprovedIssues(
  force = false,
  signal?: AbortSignal
): Promise<Issue[]> {
  if (!force) {
    const cached = readCache<Issue[]>(CACHE_KEY, TTL_MS)
    if (cached) return cached
  }
  const timeoutSignal = AbortSignal.timeout(FETCH_TIMEOUT_MS)
  const combined = signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal
  const res = await fetch(ISSUES_ENDPOINT, {
    headers: { Accept: 'application/json' },
    signal: combined,
  })
  if (!res.ok) throw new Error(`issues fetch failed: ${res.status}`)
  const data = (await res.json()) as Issue[]
  writeCache(CACHE_KEY, data)
  return data
}

export function refreshApprovedIssues(signal?: AbortSignal): Promise<Issue[]> {
  clearCache(CACHE_KEY)
  return fetchApprovedIssues(true, signal)
}

export type IssueKind = 'request' | 'feedback' | 'all'
export type IssueTarget = 'all' | 'module' | 'course' | 'lesson' | 'doc'
export type IssueSort = 'new' | 'top'

export function filterIssues(
  issues: Issue[],
  kind: IssueKind,
  target: IssueTarget
): Issue[] {
  return issues.filter((issue) => {
    const labels = issue.labels.map((l) => l.name)
    if (kind !== 'all' && !labels.includes(`type:${kind}`)) return false
    if (target !== 'all' && !labels.includes(`target:${target}`)) return false
    return true
  })
}

export function sortIssues(issues: Issue[], by: IssueSort): Issue[] {
  const copy = [...issues]
  if (by === 'top') {
    copy.sort((a, b) => b.reactions.total_count - a.reactions.total_count)
  } else {
    copy.sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
  }
  return copy
}

export function getIssueKind(issue: Issue): 'request' | 'feedback' | null {
  for (const { name } of issue.labels) {
    if (name === 'type:request') return 'request'
    if (name === 'type:feedback') return 'feedback'
  }
  return null
}

export function getIssueTarget(issue: Issue): string | null {
  for (const { name } of issue.labels) {
    if (name.startsWith('target:')) return name.slice('target:'.length)
  }
  return null
}
